import React, { useRef, Dispatch, SetStateAction, useMemo } from 'react';
import { getSaleTypeTrackingValue, SaleType } from '@models/Products';
import * as styles from './ProductsMarketFilter.module.scss';
import {
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  ChartInteractionTrackingFunctionParamSubset,
  RetailerClientStateKey,
  useGenerateChartInteractionTrackingParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { PERFORMANCE_TREND_TITLE } from '@pages/Utilities';
import { capitalize } from 'lodash';
import { FactRevenue } from '@utils/pageConfig/Types';
import { constructCurrencyLabel } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { WithHiddenListBox } from '@components/design';

interface Props {
  selectedLineChartSaleType: SaleType;
  setSelectedLineChartSaleType: Dispatch<SetStateAction<SaleType>>;
  changeHandler?: (value: string) => void;
  tooltipComponent?: JSX.Element;
  factRevenue: FactRevenue;
  volume?: string;
  retailerClientStateKey?: RetailerClientStateKey;
  isLoading?: boolean;
}

const ProductsMarketFilter: React.FC<Props> = ({
  selectedLineChartSaleType,
  setSelectedLineChartSaleType,
  changeHandler,
  tooltipComponent,
  factRevenue,
  volume = DEFAULT_VOLUME,
  retailerClientStateKey = 'hitlistsRetailerClient',
  isLoading,
}) => {
  const chartInteractionParam = useGenerateChartInteractionTrackingParam(retailerClientStateKey);
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const { access, pageType } = usePageConfig();

  const { clearExportImage } = useExportImages(pageType);

  const dispSaleType = useRef(selectedLineChartSaleType);
  const options = [
    {
      label: `Revenue${currencyLabel}`,
      value: SaleType.revenue,
    },
    {
      label: volume,
      value: SaleType.volume,
    },
    {
      label: `Price${currencyLabel}`,
      value: SaleType.price,
    },
  ];

  const chartInteractionTracking = (
    val: string,
    chartInteractionTrackingFunctionParamSubset: ChartInteractionTrackingFunctionParamSubset,
  ) => {
    //Start Chart Interaction Tracking

    const customChartInteractionParam = {
      ...chartInteractionTrackingFunctionParamSubset,
      optionChosen: capitalize(val),
      chartName: PERFORMANCE_TREND_TITLE,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.RADIO,
      access: access,
    } as ChartInteractionTrackingFunctionParam;

    chartInteractionTrackingFunction(customChartInteractionParam);
    //End Chart Interaction Tracking
  };

  const getOptionLabel = (availableOptions: any[], value: string) => {
    const option = availableOptions.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowLabelOption = useMemo(
    () => getOptionLabel(options, selectedLineChartSaleType),
    [options, selectedLineChartSaleType],
  );

  return (
    <div data-testid="products-market-filter" className={styles.ProductsMarketFilter__Header}>
      <div className={styles.ProductsMarketFilter__Header_Selector}>
        <WithHiddenListBox
          isDisabled={isLoading}
          label="Show"
          options={options}
          value={memoizedSelectedShowLabelOption}
          onChange={({ value }) => {
            if (dispSaleType.current !== value) {
              dispSaleType.current = value as SaleType;
              changeHandler?.(value);
              setSelectedLineChartSaleType(value as SaleType);
              clearExportImage();
            }
            chartInteractionTracking(getSaleTypeTrackingValue(volume)[value as SaleType], chartInteractionParam);
          }}
        />
        {tooltipComponent}
      </div>
    </div>
  );
};

export default ProductsMarketFilter;
